import { QueryResult } from 'react-query';
import moment from 'moment-timezone';
import { defaultSmDate } from 'utils/formatter';
import store from 'store';
import { ArtikelCalculator } from 'hooks/useArtikelCalculator';
import { callApi, Controller, useApi, UseApiOptions } from './useApi';
// eslint-disable-next-line import/no-cycle
import { getKunde, Kunde } from './kunde';
import { addBestellungPosition, BestellungPosition, defaultBestellungPosition } from './bestellungPosition';
import { Auftrag } from './auftrag';
import { AuftragPosition } from './auftragPosition';

export interface Bestellung {
    kNr?: number;
    nr: string;
    auNr: string;
    kaNr: number;
    kaName: string;
    status: number;
    datum: string;
    datBis: string;
    nrExt: string;
    datumExt: string;
    betreuer: number;
    rahmen: boolean;
    txt: string;
    txtB1: number;
    txtB2: number;
    txtB3: number;
    txtB4: number;
    liefBed: number;
    liefDat: string;
    liefZeit: number;
    liefKw: number;
    zahlBed: number;
    mwstCode: number;
    sNetto: number;
    sMwst: number;
    sBrutto: number;
    aktText: string;
    lagerIntNr: number;
    abrufZu: string;
    anlageDat?: string;
    anlageUhr?: number;
    anlageUser?: string;
    updDat?: string;
    updUhr?: number;
    updUser?: string;

    kunde?: Kunde;
}

export function getDefaultBestellung(kNr?: number): Bestellung {
    const state = store.getState();

    const values: Bestellung = {
        kNr: undefined,
        nr: '',
        auNr: '',
        kaNr: 0,
        kaName: '',
        status: 0,
        datum: defaultSmDate,
        datBis: defaultSmDate,
        nrExt: '',
        datumExt: defaultSmDate,
        betreuer: state.auth.nr,
        rahmen: false,
        txt: '',
        txtB1: 1,
        txtB2: 2,
        txtB3: 13,
        txtB4: 0,
        liefBed: 1,
        liefDat: defaultSmDate,
        liefZeit: 0,
        liefKw: 0,
        zahlBed: 1,
        mwstCode: 0,
        sNetto: 0,
        sMwst: 0,
        sBrutto: 0,
        aktText: '',
        lagerIntNr: 0,
        abrufZu: '',
    };

    const now = moment();

    return {
        ...values,
        kNr,
        datum: now.format('YYYY-MM-DD') + 'T00:00:00',
        // datBis: now.clone().add({ days: 7 }).format('YYYY-MM-DD') + 'T00:00:00',
    };
}

export interface BestellungFilter {
    nr?: string;
    nrSearch?: string;
    kNr?: number;
    auNr?: string;
    auNrSearch?: string;
    abrufZu?: string;
    artikelGruppeNr?: number;
    artikelNr?: string;
    searchText?: string;
    betreuer?: number;
    datumVon?: string;
    datumBis?: string;
    lArt?: string;
    status?: number;
    statusList?: number[];
    faelligeLieferungen?: boolean;
    withKunde?: boolean;
    maxRows?: number;
    sortAsc?: boolean;
}

export function useBestellung(
    filter: BestellungFilter,
    apiOptions?: UseApiOptions<Bestellung>,
): QueryResult<Bestellung> {
    return useApi<Bestellung>(Controller.bestellung + '/one', { filter }, apiOptions);
}

export function useBestellungList(
    filter: BestellungFilter,
    apiOptions?: UseApiOptions<Bestellung[]>,
): QueryResult<Bestellung[]> {
    return useApi<Bestellung[]>(Controller.bestellung + '/list', { filter }, apiOptions);
}

interface AddBestellungResult {
    newNr: string;
}

export async function addBestellung(bestellung: Bestellung): Promise<AddBestellungResult> {
    return callApi<AddBestellungResult>(Controller.bestellung + '/add', { bestellung });
}

export async function updateBestellung(bestellung: Bestellung): Promise<void> {
    return callApi<void>(Controller.bestellung + '/update', { bestellung });
}

export async function deleteBestellung(bestellung: Bestellung): Promise<void> {
    return callApi<void>(Controller.bestellung + '/delete', { bestellung });
}
export async function sendBestellung(
    bestellung: Bestellung,
    kaNrListTo: number[],
    kaNrListCC: number[],
): Promise<void> {
    return callApi<void>(Controller.bestellung + '/send', { nr: bestellung.nr, kaNrListTo, kaNrListCC });
}

export async function addBestellungFromAuftrag(
    artikelCalculator: ArtikelCalculator,
    kNr: number,
    auftrag: Auftrag,
    auftragPositionen?: AuftragPosition[],
): Promise<AddBestellungResult> {
    const newKundeList = await getKunde({ nr: kNr });

    if (newKundeList.length !== 1) {
        throw new Error('Lieferant nicht gefunden');
    }

    const newKunde = newKundeList[0];

    const defaultBestellung = getDefaultBestellung();

    const newBestellung: Bestellung = {
        ...defaultBestellung,
        kNr,
        auNr: auftrag.nr,
        betreuer: auftrag.betreuer,
        status: defaultBestellung.status,
        rahmen: auftrag.rahmen,
        datum: defaultBestellung.datum,
        datBis: defaultBestellung.datBis,
        zahlBed: newKunde.zahlZielK || defaultBestellung.zahlBed,
        aktText: auftrag.aktText,
        mwstCode: newKunde.land === 'AT' ? 20 : 0,
    };

    const result = await addBestellung(newBestellung);

    if (auftragPositionen) {
        let pos = 1;
        // eslint-disable-next-line no-restricted-syntax
        for (const auftragPosition of auftragPositionen) {
            if (auftragPosition.transfer) {
                // eslint-disable-next-line no-await-in-loop
                const { ePreis, ekPreis } = await artikelCalculator.getPreise(
                    auftragPosition.artikelGruppeNr,
                    auftragPosition.artikelNr,
                    auftragPosition.posEinh,
                );

                const netto = (auftragPosition.transferMenge || 0) * auftragPosition.posEPreis;
                const mwstFactor = auftragPosition.posMwst ? 1 + auftragPosition?.posMwst / 100 : 1;
                const brutto = auftragPosition.posMwst ? netto * mwstFactor : netto;

                const newPosition: BestellungPosition = {
                    ...defaultBestellungPosition,
                    bNr: result.newNr,
                    pos,
                    artikelGruppeNr: auftragPosition.artikelGruppeNr,
                    artikelNr: auftragPosition.artikelNr,
                    posText: auftragPosition.posText,
                    posEinh: auftragPosition.posEinh,
                    posMenge: auftragPosition.transferMenge || 0,
                    posEkPreis: ekPreis,
                    posEPreis: ePreis,
                    posNetto: netto,
                    posBrutto: brutto,
                    posMwst: newKunde.land === 'AT' ? 20 : 0,
                    posBem: auftragPosition.posBem,
                    offen: auftragPosition.transferMenge || 0,
                    lArt: auftragPosition.transferLArt || '',
                };

                // eslint-disable-next-line no-await-in-loop
                await addBestellungPosition(newPosition);
                pos += 1;
            }
        }
    }

    return result;
}

export async function addBestellungFromBestellung(
    bestellung: Bestellung,
    bestellungPositionen?: BestellungPosition[],
): Promise<AddBestellungResult> {
    const defaultBestellung = getDefaultBestellung();

    const newBestellung: Bestellung = {
        ...bestellung,
        status: defaultBestellung.status,
        datum: defaultBestellung.datum,
        rahmen: false,
        abrufZu: bestellung.nr,
    };

    const result = await addBestellung(newBestellung);
    newBestellung.nr = result.newNr;

    if (bestellungPositionen) {
        let pos = 1;
        // eslint-disable-next-line no-restricted-syntax
        for (const bestellungPosition of bestellungPositionen) {
            if (bestellungPosition.transfer) {
                // eslint-disable-next-line no-await-in-loop
                await addBestellungPosition({
                    ...bestellungPosition,
                    bNr: result.newNr,
                    pos,
                    posMenge: bestellungPosition.transferMenge || 0,
                    offen: bestellungPosition.transferMenge || 0,
                    lArt: bestellungPosition.transferLArt || '',
                });
                pos += 1;
            }
        }
    }

    return result;
}

export async function copyBestellung(
    kNr: number,
    bestellung: Bestellung,
    bestellungPositionen?: BestellungPosition[],
): Promise<AddBestellungResult> {
    const newKundeList = await getKunde({ nr: kNr });

    if (newKundeList.length !== 1) {
        throw new Error('Kunde nicht gefunden');
    }

    const newKunde = newKundeList[0];

    const defaultBestellung = getDefaultBestellung();

    const newBestellung = {
        ...bestellung,
        kNr,
        datumExt: defaultSmDate,
        nrExt: '',
        status: defaultBestellung.status,
        datum: defaultBestellung.datum,
        datBis: defaultBestellung.datBis,
        zahlBed: newKunde.zahlZielK || defaultBestellung.zahlBed,
        mwstCode: newKunde.land === 'AT' ? 20 : 0,
    };

    const result = await addBestellung(newBestellung);

    if (bestellungPositionen) {
        // eslint-disable-next-line no-restricted-syntax
        for (const bestellungPosition of bestellungPositionen) {
            // eslint-disable-next-line no-await-in-loop
            await addBestellungPosition({
                ...bestellungPosition,
                offen: bestellungPosition.posMenge,
                bNr: result.newNr,
            });
        }
    }

    return result;
}

interface RecalculateBestellungResult {
    bestellung: Bestellung;
}

export async function recalculateBestellung(bestellung: Bestellung): Promise<RecalculateBestellungResult> {
    return callApi<RecalculateBestellungResult>(Controller.bestellung + '/recalculate', { bestellung });
}

export async function finalizeBestellung(bestellung: Bestellung): Promise<RecalculateBestellungResult> {
    return callApi<RecalculateBestellungResult>(Controller.bestellung + '/finalize', { bestellung });
}
